import React, { useState, useEffect } from 'react';
import { DocumentArrowDownIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { exportApi, teachersApi } from '../services/api';
import toast from 'react-hot-toast';

interface TimetableStats {
  total_classes: number;
  total_slots: number;
  filled_slots: number;
}

interface Teacher {
  code: string;
  name: string;
  surname: string;
  title?: string;
  room?: string;
}

const OutputsPage: React.FC = () => {
  const [stats, setStats] = useState<TimetableStats>({
    total_classes: 0,
    total_slots: 0,
    filled_slots: 0
  });
  const [exporting, setExporting] = useState(false);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [venues, setVenues] = useState<string[]>([]);
  const [selectedPrintType, setSelectedPrintType] = useState<'teacher' | 'venue'>('teacher');
  const [selectedTeacher, setSelectedTeacher] = useState<string>('');
  const [selectedVenue, setSelectedVenue] = useState<string>('');
  const [printing, setPrinting] = useState(false);
  const [generatingSchedule, setGeneratingSchedule] = useState(false);
  const [generatingWorkload, setGeneratingWorkload] = useState(false);

  useEffect(() => {
    loadTimetableStats();
    loadTeachers();
    loadVenues();
  }, []);

  const loadTeachers = async () => {
    try {
      const response = await teachersApi.getAll();
      setTeachers(response.data);
    } catch (error) {
      console.error('Error loading teachers:', error);
    }
  };

  const loadVenues = async () => {
    // For now, use a static list of common venue types
    // In the future, this could be fetched from the database or derived from teacher rooms
    const staticVenues = [
      'Room 101', 'Room 102', 'Room 103', 'Room 104', 'Room 105',
      'Science Lab 1', 'Science Lab 2', 'Computer Lab',
      'Art Studio', 'Music Room', 'Gymnasium', 'Library',
      'Assembly Hall', 'Cafeteria', 'Outdoor Sports Field'
    ];
    setVenues(staticVenues);
  };

  const loadTimetableStats = async () => {
    try {
      const response = await exportApi.getTimetableStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading timetable stats:', error);
    }
  };

  const handleExportExcel = async () => {
    if (stats.total_slots === 0) {
      toast.error('No timetable data to export. Please create a schedule first.');
      return;
    }

    setExporting(true);
    try {
      const response = await exportApi.exportTimetableExcel();
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'squire_schedule_timetable_export.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Timetable exported successfully!');
    } catch (error) {
      console.error('Error exporting timetable:', error);
      toast.error('Failed to export timetable. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handlePrintTimetable = async () => {
    if (selectedPrintType === 'teacher' && !selectedTeacher) {
      toast.error('Please select a teacher to generate their timetable.');
      return;
    }
    
    if (selectedPrintType === 'venue' && !selectedVenue) {
      toast.error('Please select a venue to generate its timetable.');
      return;
    }

    setPrinting(true);
    try {
      if (selectedPrintType === 'teacher') {
        // Call the teacher timetable PDF endpoint
        const response = await fetch(`http://localhost:8000/export/teacher-timetable-pdf/${selectedTeacher}`);
        
        if (!response.ok) {
          throw new Error('Failed to generate teacher timetable');
        }
        
        // Download the PDF
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `timetable_${selectedTeacher}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('Teacher timetable generated successfully!');
      } else {
        // Venue timetable not implemented yet
        toast('Venue timetable generation will be implemented soon!');
      }
    } catch (error) {
      console.error('Error generating timetable:', error);
      toast.error('Failed to generate timetable. Please try again.');
    } finally {
      setPrinting(false);
    }
  };

  const handleGenerateLearnerSchedule = async () => {
    setGeneratingSchedule(true);
    try {
      const response = await exportApi.generateLearnerSchedule('Dummy');
      toast.success(response.data.message);
      console.log('Generated schedule:', response.data);
    } catch (error) {
      console.error('Error generating learner schedule:', error);
      toast.error('Failed to generate learner schedule. Please try again.');
    } finally {
      setGeneratingSchedule(false);
    }
  };

  const handleGenerateWorkloadReport = async () => {
    setGeneratingWorkload(true);
    try {
      const response = await fetch('http://localhost:8000/export/teacher-workload-report/');
      
      if (!response.ok) {
        throw new Error('Failed to generate workload report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'teacher_workload_report.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Teacher workload report generated successfully!');
    } catch (error) {
      console.error('Error generating workload report:', error);
      toast.error('Failed to generate workload report. Please try again.');
    } finally {
      setGeneratingWorkload(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center space-x-3">
        <DocumentArrowDownIcon className="w-8 h-8 text-semantic-info" />
        <h1 className="text-h1 font-montserrat text-northwood-navy">Outputs</h1>
      </div>

      {/* Two-column layout for Export and Print sections */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Export Timetable Section */}
        <div className="card">
          <h2 className="text-h3 font-montserrat text-northwood-navy mb-6">
            Export Timetable
          </h2>

          <div className="space-y-6">
            <div className="p-6 border border-neutral-silver rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-montserrat font-semibold text-northwood-navy mb-2">
                    Excel Export
                  </h3>
                  <p className="text-neutral-medium-gray text-sm mb-2">
                    Download complete timetable with all assignments and details
                  </p>
                  <div className="mt-2 text-xs text-neutral-medium-gray">
                    Includes: Grade, Class, Teacher 1 code, Teacher 2 code, Subject, Code, Subject Section, Day No, Period No, Period Len, Room
                  </div>
                  <div className="mt-1 text-xs text-accent-teal font-medium">
                    Ed-Admin format
                  </div>
                </div>
                <button 
                  onClick={handleExportExcel}
                  disabled={exporting || stats.total_slots === 0}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exporting ? 'Exporting...' : 'Download Excel'}
                </button>
              </div>
            </div>

            <div className="p-4 bg-semantic-warning bg-opacity-10 border border-semantic-warning rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-semantic-warning mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-montserrat font-semibold text-semantic-warning text-sm mb-1">
                    Export Requirements
                  </h4>
                  <p className="text-semantic-warning text-sm">
                    Ensure your timetable is properly configured with class assignments before exporting.
                    Each slot will be exported as a separate row in the Excel file.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 p-4 bg-neutral-light-gray rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-montserrat font-bold text-northwood-navy">
                  {stats.total_classes}
                </div>
                <div className="text-sm text-neutral-medium-gray">Total Classes</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-montserrat font-bold text-accent-teal">
                  {stats.total_slots}
                </div>
                <div className="text-sm text-neutral-medium-gray">Total Slots</div>
              </div>
            </div>

            {stats.total_slots === 0 && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-montserrat font-semibold text-blue-700 text-sm mb-1">
                      No Timetable Data
                    </h4>
                    <p className="text-blue-600 text-sm">
                      Go to the Timetable Packing page to create your schedule before exporting.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Print Timetable Section */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <PrinterIcon className="w-6 h-6 text-accent-gold" />
            <h2 className="text-h3 font-montserrat text-northwood-navy">
              Print Timetable
            </h2>
          </div>

          <div className="space-y-6">
            {/* Print Type Selection */}
            <div>
              <label className="block text-sm font-montserrat font-semibold text-northwood-navy mb-3">
                Select Timetable Type
              </label>
              <div className="grid grid-cols-1 gap-3">
                <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-neutral-light-gray">
                  <input
                    type="radio"
                    name="printType"
                    value="teacher"
                    checked={selectedPrintType === 'teacher'}
                    onChange={(e) => setSelectedPrintType(e.target.value as 'teacher' | 'venue')}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-montserrat font-medium text-northwood-navy">Teacher Timetable</div>
                    <div className="text-xs text-neutral-medium-gray">Individual teacher schedule</div>
                  </div>
                </label>
                <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-neutral-light-gray">
                  <input
                    type="radio"
                    name="printType"
                    value="venue"
                    checked={selectedPrintType === 'venue'}
                    onChange={(e) => setSelectedPrintType(e.target.value as 'teacher' | 'venue')}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-montserrat font-medium text-northwood-navy">Venue Timetable</div>
                    <div className="text-xs text-neutral-medium-gray">Room/venue usage schedule</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Teacher Selection */}
            {selectedPrintType === 'teacher' && (
              <div>
                <label className="block text-sm font-montserrat font-semibold text-northwood-navy mb-2">
                  Select Teacher
                </label>
                <select
                  value={selectedTeacher}
                  onChange={(e) => setSelectedTeacher(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-silver rounded-md focus:outline-none focus:ring-2 focus:ring-accent-teal"
                >
                  <option value="">Choose a teacher...</option>
                  {teachers.map((teacher) => (
                    <option key={teacher.code} value={teacher.code}>
                      {teacher.title} {teacher.name} {teacher.surname} ({teacher.code})
                      {teacher.room && ` - ${teacher.room}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Venue Selection */}
            {selectedPrintType === 'venue' && (
              <div>
                <label className="block text-sm font-montserrat font-semibold text-northwood-navy mb-2">
                  Select Venue
                </label>
                <select
                  value={selectedVenue}
                  onChange={(e) => setSelectedVenue(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-silver rounded-md focus:outline-none focus:ring-2 focus:ring-accent-teal"
                >
                  <option value="">Choose a venue...</option>
                  {venues.map((venue) => (
                    <option key={venue} value={venue}>
                      {venue}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Generate Button */}
            <div className="flex flex-col space-y-3 p-4 bg-neutral-light-gray rounded-lg">
              <div>
                <h4 className="font-montserrat font-medium text-northwood-navy mb-1">
                  Generate PDF Timetable
                </h4>
                <p className="text-sm text-neutral-medium-gray">
                  Creates a landscape PDF with a neat table format showing the 10-day cycle schedule
                </p>
              </div>
              <button
                onClick={handlePrintTimetable}
                disabled={printing || (selectedPrintType === 'teacher' && !selectedTeacher) || (selectedPrintType === 'venue' && !selectedVenue)}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed w-full"
              >
                {printing ? 'Generating...' : 'Generate PDF'}
              </button>
            </div>

            {/* Info Box */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <PrinterIcon className="w-5 h-5 text-blue-500 mt-0.5" />
                </div>
                <div>
                  <h4 className="font-montserrat font-semibold text-blue-700 text-sm mb-1">
                    PDF Format Preview
                  </h4>
                  <p className="text-blue-600 text-sm mb-2">
                    The generated PDF will include:
                  </p>
                  <ul className="text-blue-600 text-xs space-y-1 list-disc list-inside">
                    <li>10-day cycle grid (Days 1-10 × Periods 1-6)</li>
                    <li>{selectedPrintType === 'teacher' ? 'All classes assigned to the selected teacher' : 'All classes scheduled in the selected venue'}</li>
                    <li>Class codes, subject information, and grades</li>
                    <li>Landscape orientation for optimal viewing</li>
                    <li>School branding and header information</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Generate Dummy Learner Schedule Section */}
      <div className="card">
        <h2 className="text-h3 font-montserrat text-northwood-navy mb-6">
          Generate Dummy Learner Schedule
        </h2>

        <div className="space-y-6">
          <div className="p-6 border border-neutral-silver rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-montserrat font-semibold text-northwood-navy mb-2">
                  Student Schedule Generator
                </h3>
                <p className="text-neutral-medium-gray text-sm mb-2">
                  Generate sample student schedules based on the current timetable
                </p>
                <p className="text-neutral-light-gray text-xs">
                  This will create realistic student schedules showing how learners move between classes throughout the 10-day cycle
                </p>
              </div>
              <button
                onClick={handleGenerateLearnerSchedule}
                className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={stats.total_slots === 0 || generatingSchedule}
              >
                <DocumentArrowDownIcon className="w-5 h-5" />
                <span>{generatingSchedule ? 'Generating...' : 'Generate Dummy Learner Schedule'}</span>
              </button>
            </div>
          </div>

          {stats.total_slots === 0 && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-montserrat font-semibold text-blue-700 text-sm mb-1">
                    No Timetable Data
                  </h4>
                  <p className="text-blue-600 text-sm">
                    Create a timetable first before generating learner schedules.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Timetable Analysis Section */}
      <div className="card mt-8">
        <h2 className="text-h3 font-montserrat text-northwood-navy mb-6">
          Timetable Analysis
        </h2>

        <div className="space-y-4">
          <div className="p-6 border border-neutral-silver rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-montserrat font-semibold text-northwood-navy mb-2">
                  Teacher Workload Analysis
                </h3>
                <p className="text-neutral-medium-gray text-sm">
                  Analyze teaching loads, contact hours, and distribution across the timetable
                </p>
              </div>
              <button
                onClick={handleGenerateWorkloadReport}
                disabled={generatingWorkload}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generatingWorkload ? 'Generating...' : 'Generate Teacher Workload Report'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutputsPage;